/**
 * DeveloperPortalPage — Fix-Agent Developer-Redesign (v2-final/developer.html)
 *
 * Konsolidiertes Developer-Portal auf /developer gemaess Design-Referenz:
 * v2-Basis-URL + Kontingent-Balken im Header-Kasten, API-Token-Verwaltung
 * (Erstellen/Rotieren/Widerrufen inkl. Einmal-Reveal-Modals), Webhook-
 * Konfiguration mit Test-Payload-Button, sowie ein Quick-Start-Codebeispiel.
 *
 * ACCEPTANCE-Fixes ggue. der vorherigen Fassung:
 * - "v2 base URL only": das alte v1-"Api-Key"-Konzept (ApiKeyCard,
 *   CodeExamplesCard mit "Api-Key"-Header gegen die v1-Public-API) ist von
 *   dieser Seite entfernt (bleibt unberuehrt fuer die stabile Public-API auf
 *   der separaten /api-docs-Seite). Quick Start zeigt nur noch den
 *   v2-Aufruf mit Bearer-API-Token.
 * - "uniform premium gating": EIN Zugangs-Check (useApiUsage) am
 *   Portal-Wurzelknoten statt drei bis vier ad-hoc Sperr-Anzeigen
 *   (ApiKeyCard-Text, ApiQuotaCard-Upsell, je ein PremiumRequired in
 *   ApiTokensPage/WebhooksPage) — Tokens/Webhooks werden erst gemountet,
 *   wenn der Zugang feststeht.
 * - "usage meter": prominent im Header-Kasten (ApiBaseUrlUsageCard) plus
 *   Detailkarte (ApiQuotaCard: Reset-Datum, Rate-Limit, Tarif).
 *
 * Contract-Hinweise (dokumentierte Luecken statt erfundener Endpunkte):
 * - ApiToken kennt im Contract KEINE Scopes — die "Scopes"-Pills der
 *   Design-Vorlage entfallen daher.
 * - Es gibt KEINEN PATCH/PUT auf /api/v2/webhooks/:id — der "Bearbeiten"-
 *   Button der Vorlage entfaellt; Aenderungen laufen ueber Loeschen+Neuanlegen.
 */
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ApiBaseUrlUsageCard } from './ApiBaseUrlUsageCard'
import { ApiQuotaCard } from './ApiQuotaCard'
import { QuickStartCard } from './QuickStartCard'
import { ApiTokensPage } from './ApiTokensPage'
import { WebhooksPage } from './WebhooksPage'
import { useApiUsage } from './useApiUsage'
import { PremiumRequired } from '../../components/PremiumRequired'
import './developer-portal.css'

export function DeveloperPortalPage() {
  const { t } = useTranslation('developer')
  const { usage, isLoading, isError, refetch } = useApiUsage()

  return (
    <main className="developer-portal" data-testid="developer-portal">
      <header
        className="section-header"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0 }}>{t('portal.title')}</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            {t('portal.subtitle')}
          </p>
        </div>
        <Link
          to="/api-docs"
          className="btn"
          data-testid="portal-api-docs-link"
          style={{ flexShrink: 0 }}
        >
          {t('portal.apiDocsLink')}
        </Link>
      </header>

      {isLoading && (
        <div
          data-testid="portal-loading"
          aria-busy="true"
          aria-label={t('apiQuota.loading')}
          style={{ height: 64, borderRadius: 10, background: 'color-mix(in srgb, var(--text) 8%, transparent)', animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }}
        />
      )}

      {!isLoading && isError && (
        <div role="alert" data-testid="portal-error" className="card card-pad">
          <p style={{ margin: '0 0 12px', fontSize: 13 }}>{t('apiQuota.error')}</p>
          <button type="button" className="btn btn-sm btn-primary" onClick={refetch}>
            {t('common:actions.retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && usage && !usage.api_access && (
        <PremiumRequired message={t('portal.gatingMessage')} data-testid="portal-premium-required" />
      )}

      {!isLoading && !isError && usage && usage.api_access && (
        <>
          <ApiBaseUrlUsageCard usage={usage} />

          <div className="dev-portal-grid">
            <div className="dev-portal-col" data-testid="portal-col-main">
              <section
                className="card card-pad dev-portal-section"
                data-testid="portal-tokens-section"
                aria-label={t('apiTokens.pageTitle')}
              >
                <ApiTokensPage variant="embedded" />
              </section>

              <section
                className="card card-pad dev-portal-section"
                data-testid="portal-webhooks-section"
                aria-label={t('webhooks.pageTitle')}
              >
                <WebhooksPage variant="embedded" />
              </section>
            </div>
            <div className="dev-portal-col" data-testid="portal-col-side">
              <QuickStartCard />
              <ApiQuotaCard />
            </div>
          </div>
        </>
      )}
    </main>
  )
}
